import React, { useMemo, useState } from 'react';
import { FileSpreadsheet, Paperclip, Pencil, Trash2, Layers, DollarSign, Calendar, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { formatCurrency, formatDate } from './utils';

/** Extrae el grupo de abono del campo observaciones. */
const getGrupoId = (obs) => {
  if (!obs) return null;
  const m = String(obs).match(/\[ABONO:([^\]]+)\]/);
  return m ? m[1] : null;
};

/** Extrae la descripción limpia (sin prefijos técnicos). */
const cleanDescripcion = (obs) => {
  if (!obs) return '';
  return String(obs)
    .replace(/\[ABONO:[^\]]+\]/g, '')
    .replace(/\[MON:[^\]]+\]/g, '')
    .replace(/\[TASA:[^\]]+\]/g, '')
    .replace(/\[USD:[^\]]+\]/g, '')
    .trim();
};

/** Extrae metadata de moneda/tasa desde el pago (usa campos backend si existen). */
const readMoneda = (p) => {
  if (p?.moneda) {
    return { moneda: p.moneda, tasa: p.tasa_cambio ?? null, monto_original: p.monto_original ?? null };
  }
  const obs = String(p?.observaciones || '');
  const mMon = obs.match(/\[MON:([A-Z]+)\]/);
  const mTasa = obs.match(/\[TASA:([0-9.]+)\]/);
  const mUsd = obs.match(/\[USD:([0-9.]+)\]/);
  return {
    moneda: mMon ? mMon[1] : 'DOP',
    tasa: mTasa ? parseFloat(mTasa[1]) : null,
    monto_original: mUsd ? parseFloat(mUsd[1]) : null,
  };
};

/**
 * Agrupa los pagos por `abono_grupo_id`. Cada grupo se presenta como una sola
 * fila en el historial, mostrando todas las facturas cubiertas. Pagos sin
 * grupo (flujo antiguo por-factura) se mantienen como filas individuales.
 */
export const HistorialPagosTab = ({
  pagos,
  onExportarExcel,
  onVerComprobantes,
  onEditPago,
  onDeletePago,
}) => {
  // Filtros de fecha (Desde / Hasta) para acotar el historial
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Aplicar filtro de fechas ANTES de agrupar
  const pagosFiltrados = useMemo(() => {
    if (!fechaDesde && !fechaHasta) return pagos || [];
    return (pagos || []).filter((p) => {
      const fecha = String(p.fecha || '').slice(0, 10);
      if (!fecha) return false;
      if (fechaDesde && fecha < fechaDesde) return false;
      if (fechaHasta && fecha > fechaHasta) return false;
      return true;
    });
  }, [pagos, fechaDesde, fechaHasta]);

  const grupos = useMemo(() => {
    const map = new Map();
    pagosFiltrados.forEach((p) => {
      const gid = getGrupoId(p.observaciones);
      const key = gid || `single-${p.id || p._id}`;
      if (!map.has(key)) {
        const meta = readMoneda(p);
        map.set(key, {
          key,
          grupo_id: gid,
          es_grupo: !!gid,
          fecha: p.fecha,
          cliente_id: p.cliente_id,
          cliente_nombre: p.cliente_nombre,
          metodo_pago: p.metodo_pago,
          referencia: p.referencia,
          descripcion: cleanDescripcion(p.observaciones),
          moneda: meta.moneda,
          tasa: meta.tasa,
          monto_original: meta.monto_original,
          comprobantes: Array.isArray(p.comprobantes) ? [...p.comprobantes] : [],
          items: [],
        });
      }
      const g = map.get(key);
      g.items.push(p);
      // Combinar comprobantes de todos los pagos del grupo, sin duplicados por URL
      if (Array.isArray(p.comprobantes)) {
        p.comprobantes.forEach((c) => {
          const url = c && typeof c === 'object' ? c.url : c;
          if (url && !g.comprobantes.some((x) => (x && typeof x === 'object' ? x.url : x) === url)) {
            g.comprobantes.push(c);
          }
        });
      }
    });
    // Ordenar por fecha desc
    return Array.from(map.values()).sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || '')));
  }, [pagosFiltrados]);

  // Total DOP visible en el rango filtrado (rápida referencia)
  const totalDopVisible = useMemo(
    () => pagosFiltrados.reduce((s, p) => s + Number(p.monto || 0), 0),
    [pagosFiltrados],
  );

  const limpiarFiltros = () => {
    setFechaDesde('');
    setFechaHasta('');
  };
  const hayFiltros = !!fechaDesde || !!fechaHasta;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base md:text-lg font-display font-600">
              Historial de Pagos Recientes
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={onExportarExcel}
              disabled={pagosFiltrados.length === 0}
              data-testid="btn-export-pagos-excel"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
          </div>
          {/* Filtros de fecha */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3 pt-1">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span className="font-medium">Filtrar por fecha:</span>
            </div>
            <div className="grid grid-cols-2 sm:flex sm:items-end gap-2 flex-1">
              <div>
                <Label htmlFor="hist-desde" className="text-[11px] text-muted-foreground">Desde</Label>
                <Input
                  id="hist-desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  className="h-9 w-full sm:w-[160px] mt-0.5"
                  data-testid="hist-filtro-desde"
                />
              </div>
              <div>
                <Label htmlFor="hist-hasta" className="text-[11px] text-muted-foreground">Hasta</Label>
                <Input
                  id="hist-hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  className="h-9 w-full sm:w-[160px] mt-0.5"
                  data-testid="hist-filtro-hasta"
                />
              </div>
              {hayFiltros && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={limpiarFiltros}
                  className="h-9 gap-1 self-end col-span-2 sm:col-span-1"
                  data-testid="hist-filtro-limpiar"
                >
                  <X className="w-3.5 h-3.5" /> Limpiar
                </Button>
              )}
            </div>
            {hayFiltros && (
              <div className="flex flex-wrap items-center gap-2 sm:ml-auto text-xs">
                <Badge variant="secondary" className="gap-1 tabular-nums" data-testid="hist-filtro-conteo">
                  {pagosFiltrados.length} pago{pagosFiltrados.length === 1 ? '' : 's'}
                </Badge>
                <Badge variant="outline" className="gap-1 tabular-nums text-green-700 border-green-300 bg-green-50">
                  Total: {formatCurrency(totalDopVisible)}
                </Badge>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="tabla-historial-pagos">
            <thead>
              <tr className="border-b text-left text-sm text-muted-foreground">
                <th className="pb-3 font-medium">Fecha</th>
                <th className="pb-3 font-medium">Cliente</th>
                <th className="pb-3 font-medium">Facturas cubiertas</th>
                <th className="pb-3 font-medium text-right">Monto DOP</th>
                <th className="pb-3 font-medium">Método / Moneda</th>
                <th className="pb-3 font-medium">Referencia</th>
                <th className="pb-3 font-medium text-center">Comprob.</th>
                <th className="pb-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {grupos.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-8 text-center text-muted-foreground">
                    {hayFiltros
                      ? 'No hay pagos en el rango de fechas seleccionado'
                      : 'No hay pagos registrados'}
                  </td>
                </tr>
              ) : (
                grupos.map((g) => {
                  const totalDop = g.items.reduce((s, p) => s + Number(p.monto || 0), 0);
                  const esUsd = g.moneda === 'USD' && g.tasa && g.monto_original;
                  return (
                    <tr key={g.key} className="border-b hover:bg-accent/60 transition-colors align-top">
                      <td className="py-3 whitespace-nowrap">{formatDate(g.fecha)}</td>
                      <td className="py-3 font-medium">{g.cliente_nombre}</td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1 max-w-md">
                          {g.items.map((p, idx) => (
                            <Badge
                              key={`${p.id}-${idx}`}
                              variant="outline"
                              className="text-[11px] font-medium tabular-nums"
                              title={`${p.factura_numero}: ${formatCurrency(p.monto)}`}
                            >
                              {p.factura_numero}
                            </Badge>
                          ))}
                          {g.es_grupo && g.items.length > 1 && (
                            <Badge className="text-[10px] gap-1 bg-primary/15 text-primary border-primary/30 hover:bg-primary/15">
                              <Layers className="w-3 h-3" /> {g.items.length} facturas
                            </Badge>
                          )}
                        </div>
                        {g.descripcion && (
                          <div className="mt-1 text-[11px] text-muted-foreground italic">
                            &ldquo;{g.descripcion}&rdquo;
                          </div>
                        )}
                      </td>
                      <td className="py-3 text-right tabular-nums font-semibold text-green-700 whitespace-nowrap">
                        {formatCurrency(totalDop)}
                        {esUsd && (
                          <div className="text-[10px] font-normal text-muted-foreground mt-0.5">
                            USD {Number(g.monto_original).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} × {Number(g.tasa).toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline" className="w-fit">{g.metodo_pago}</Badge>
                          <Badge
                            variant="outline"
                            className={`w-fit text-[10px] gap-1 ${esUsd ? 'bg-blue-50 text-blue-800 border-blue-300' : 'bg-emerald-50 text-emerald-800 border-emerald-300'}`}
                          >
                            <DollarSign className="w-3 h-3" /> {g.moneda}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3 text-muted-foreground text-xs">{g.referencia || '—'}</td>
                      <td className="py-3 text-center">
                        {g.comprobantes.length > 0 ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onVerComprobantes({ ...g.items[0], comprobantes: g.comprobantes })}
                            title={`Ver ${g.comprobantes.length} comprobante(s)`}
                            className="h-8 px-2 gap-1"
                            data-testid={`btn-ver-comprobantes-${g.key}`}
                          >
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="text-xs tabular-nums">{g.comprobantes.length}</span>
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditPago(g)}
                          title="Editar pago"
                          data-testid={`btn-edit-pago-${g.key}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeletePago(g)}
                          title="Eliminar pago"
                          data-testid={`btn-delete-pago-${g.key}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default HistorialPagosTab;
